import React from 'react';
import clsx from 'clsx';
import styles from './App.module.scss';

import SearchBar from './components/SearchBar';
import FloatingToggleButton from './components/FloatingToggleButton';
import SideBar from './components/SideBar';
import Map from './components/Map';

import {
	AGENCY_PROJECT,
	CHAR_SEPARATOR,
	DATA_SRC_TYPES,
	FILIALI_PROJECT,
	MOBILE_INFOWINDOW_VIEW,
	PAGE_CALCULATE_ROUTE,
	PAGE_LANDING,
	PAGE_SERP_LIST,
	PAGE_SERP_MAP,
	PAGE_STREET_VIEW,
	PROJECT_CAR,
	PROJECT_CRI,
	PROJECT_CSV,
	TRAVEL_MODES
} from './utils/Constants';
import Filters from './components/Filters';
import ResultList from './components/ResultList';
import Icon from './components/Icon';
import { useCalcRoute, useGoogleMapsApi, useStreetView } from './api/gis';
import { useAutocompleteResultSearch, useGeoCMSMap } from './api/geocms';
import Loading from './components/Loading';
import { useDeviceDetection } from './utils/DeviceDetection';
import { clearMap, isMunicipality } from './api/geocmsUtil';
import StreetViewController from './components/StreetViewController';
import Tabs from './components/Tabs';
import InfoWindow from './components/InfoWindow';
import { useInputParams } from './utils/InputParamsProvider';
import { useTCLog } from './utils/TCEvent';
import Detail from './components/Detail';
import { useSearchProcess } from './api/searchProcess';
import {
	COLOR_PALETTE,
	SHOW_ALL,
	TABS_TO_DISPLAY
} from './api/portletConfiguration';
import { useDataSources } from './api/dataSources';

export default function App() {
	const [, setTCAppData] = useTCLog();

	const google = useGoogleMapsApi();
	const [RANKING, , DATA] = useInputParams();
	const [, getDataSource] = useDataSources();

	const cookieLat = getCookie('lat');
	const cookieLng = getCookie('lng');
	const cookieAddress = getCookie('formattedAddress');

	let initialSearchParameters = null;
	let initialInputValue = '';
	let initialPage = PAGE_LANDING;

	if (cookieLat && cookieLng) {
		initialSearchParameters = {
			lat: parseFloat(cookieLat),
			lng: parseFloat(cookieLng),
			formattedAddress: cookieAddress || '',
			place: null
		};
		initialInputValue = cookieAddress || '';
		initialPage = PAGE_SERP_MAP;
	}

	const [page, setPage] = React.useState(initialPage);
	const [searchFor, setSearchFor] = React.useState(
		DATA ? DATA : TABS_TO_DISPLAY[0]
	);

	const [mainInputValue, setMainInputValue] = React.useState(initialInputValue);
	const [geoLocationEnabled, setGeoLocationEnabled] = React.useState(false);

	const [rankedSearch, setRankedSearch] = React.useState(false);
	const [rankedSorting, setRankedSorting] = React.useState(false);

	const [showFilters, setShowFilters] = React.useState(false);
	//const [searchParameters, setSearchParameters] = React.useState(null);
	const [searchParameters, setSearchParameters] = React.useState(initialSearchParameters);



	const dataSrc = getDataSource(searchFor);
	const showFiltersButton =
		SHOW_ALL ||
			(dataSrc && dataSrc.type !== DATA_SRC_TYPES.GeoCMS) ||
			(dataSrc && dataSrc.key === FILIALI_PROJECT)
			? false
			: true;

	const [autocompleteResult, setAutocompleteResult] = React.useState(null);
	const [focusedResult, setFocusedResult] = React.useState(null);
	const [calcRouteResult, setCalcRouteResult] = React.useState(null);
	const [calcRouteCustomParams, setCalcRouteCustomParams] = React.useState(
		null
	);
	const [skipCalcRoute, setSkipCalcRoute] = React.useState(false);

	const [
		calcRouteByDrivingResponse,
		setCalcRouteByDrivingResponse
	] = React.useState();
	const [
		calcRouteByTransitgResponse,
		setCalcRouteByTransitResponse
	] = React.useState();
	const [
		calcRouteByWalkingResponse,
		setCalcRouteByWalkingResponse
	] = React.useState();

	const [loading, setLoading] = React.useState(false);

	const [streetViewResult, setStreetViewResult] = React.useState(null);

	const [detailResult, setDetailResult] = React.useState(null);

	React.useEffect(() => {
		if (searchFor === AGENCY_PROJECT) {
			setRankedSearch(true);
		} else {
			setRankedSearch(false);
		}
	}, [searchFor]);

	React.useEffect(() => {
		setTCAppData(page, searchFor, geoLocationEnabled);
	}, [page, searchFor, geoLocationEnabled, setTCAppData]);

	const isMobile = useDeviceDetection();

	function handleGeoLocationEnable() {
		setGeoLocationEnabled(true);
	}

	function handleCalcRouteParamsChange({
		startFormattedAddress,
		endLat,
		endLng
	}) {
		const _calcRouteCustomParams = {};

		if (startFormattedAddress) {
			_calcRouteCustomParams.start = startFormattedAddress;
		} else if (calcRouteCustomParams && calcRouteCustomParams.start) {
			_calcRouteCustomParams.start = calcRouteCustomParams.start;
		}

		if (endLat && endLng) {
			_calcRouteCustomParams.end = { lat: endLat, lng: endLng };
		} else if (calcRouteCustomParams && calcRouteCustomParams.end) {
			_calcRouteCustomParams.end = calcRouteCustomParams.end;
		}

		setCalcRouteCustomParams(_calcRouteCustomParams);
	}

	function handlePageChange(page) {
		setPage(page);
	}

	function handleSearchForChange(searchFor) {
		setSearchFor(searchFor);
		setFocusedResult(null);
		if (page === MOBILE_INFOWINDOW_VIEW) setPage(PAGE_SERP_MAP);
	}

	function handleSearchSubmit({ lat, lng, formattedAddress, place }) {
		setSearchParameters({ lat, lng, formattedAddress, place });

		setPage((page) => {
			if (page === PAGE_LANDING) {
				return PAGE_SERP_MAP;
			} else {
				return page;
			}
		});
	}

	function handleCalcRouteClick(result) {
		if (result) {
			const latlng = {
				lat: parseFloat(result.lat),
				lng: parseFloat(result.lng)
			};

			const geocoder = new google.maps.Geocoder();
			geocoder.geocode({ location: latlng }, (results, status) => {
				if (status === 'OK') {
					if (results[0]) {
						const description = results[0].formatted_address;

						result.formattedAddress = description;
						setCalcRouteResult(result);
						setPage(PAGE_CALCULATE_ROUTE);
					}
				} else {
					console.error('Geocoder failed due to: ' + status);
				}
			});
		} else {
			setCalcRouteResult(null);
			setPage(PAGE_SERP_MAP);
		}
	}

	function handleStreetViewClick(result) {
		setStreetViewResult(result);
	}

	function handleDetailClick(result) {
		setDetailResult(result);
	}

	function onToggleFilters() {
		setShowFilters(!showFilters);
	}

	function handleCloseDetail() {
		setDetailResult(null);
	}

	const searchBarRef = React.useRef();

	const sideBarRef = React.useRef();

	const [googleMap, setGoogleMap] = React.useState(null);
	const [streetView, setStreetView] = React.useState();

	React.useEffect(() => {
		if (!googleMap) return;

		const streetViewOptions = {
			addressControl: false,
			enableCloseButton: false
		};

		const _streetView = googleMap.getStreetView();
		_streetView.setOptions(streetViewOptions);
		setStreetView(_streetView);
	}, [googleMap]);

	const [geoCMSMap] = useGeoCMSMap(googleMap);

	const [results, filters, setFilters] = useSearchProcess(
		geoCMSMap,
		searchFor,
		searchParameters,
		setLoading
	);

	const sortedResults = React.useMemo(() => {
		if (!results) return null;

		const isRankingActive = searchFor === AGENCY_PROJECT && rankedSorting;
		console.log(`[Sorting] Ordinamento avviato. Progetto: ${searchFor}. Modalità: ${isRankingActive ? 'RANKING' : 'CATEGORIE + DISTANZA'}`);

		// Creiamo una copia dell'array per non mutare l'originale
		const sorted = [...results].sort((a, b) => {
			// 1. Agenzia selezionata (focusedResult) sempre in cima
			if (focusedResult) {
				if (a.id === focusedResult.id) return -1;
				if (b.id === focusedResult.id) return 1;
			}

			// 2. Logica di ordinamento (ranking o distanza per categorie)
			if (isRankingActive) {
				const rankA = parseFloat(a.rank) || 0;
				const rankB = parseFloat(b.rank) || 0;
				return rankB - rankA; // Ranking decrescente (più alto = prima)
			} else {
				// Ordinamento per categoria (Main Site > Secondary Site > SubAgency) e poi per distanza
				const getTypeOrder = (item) => {
					if (item.subAgencyId) return 3; // Subagenzia
					if (item.siteId) return 2;      // Sede secondaria
					return 1;                       // Sede principale
				};

				const orderA = getTypeOrder(a);
				const orderB = getTypeOrder(b);

				if (orderA !== orderB) {
					return orderA - orderB;
				}

				const distA = parseFloat(a.distance) || Infinity;
				const distB = parseFloat(b.distance) || Infinity;
				return distA - distB;
			}
		});

		console.log('[Sorting] Risultati ordinati:', sorted.slice(0, 10).map(r => ({
			name: r.name,
			type: r.subAgencyId ? 'SUB' : (r.siteId ? 'SECONDARIA' : 'PRINCIPALE'),
			distance: r.distance,
			rank: r.rank
		})));

		return sorted;
	}, [results, focusedResult, rankedSearch, rankedSorting]);

	const [autocompleteResults] = useAutocompleteResultSearch(
		geoCMSMap,
		searchFor,
		searchParameters,
		mainInputValue,
		setLoading
	);

	if (autocompleteResults) {
		autocompleteResults.sort((a, b) => {
			let compare = null;

			parseFloat(a.distance) < parseFloat(b.distance)
				? (compare = -1)
				: a.distance > b.distance
					? (compare = 1)
					: (compare = 0);

			return compare;
		});
	}

	function handleFiltersChange(filters) {
		setFilters(filters, true);
	}

	function panMapToResult(resultId) {
		if (!(resultId && googleMap)) return;

		const objectId = resultId.split(CHAR_SEPARATOR)[0];
		const projectId = resultId.split(CHAR_SEPARATOR)[2];

		const position = geoCMSMap
			.getProject(projectId)
			.getObjectLoadedById(objectId.split('-')[1])
			.getGeoElements()[0]
			.getOverlay()
			.getPosition();

		const lat = position.lat();
		const lng = position.lng();

		panMapToLatLng(lat, lng);
	}

	function panMapToLatLng(lat, lng) {
		googleMap.panTo({
			lat: lat,
			lng: lng
		});
	}

	function handleToggleInfoWindow(result) {
		if (result) {
			openInfoWindow(result);
		} else {
			closeInfoWindow();
		}
	}

	function openInfoWindow(result) {
		if (isMobile) {
			setPage(MOBILE_INFOWINDOW_VIEW);
		}

		setFocusedResult(result);
	}

	function closeInfoWindow() {
		setPage(PAGE_SERP_MAP);
		setFocusedResult(null);
	}

	const [fetchCalcRoute, resetMap] = useCalcRoute(
		googleMap,
		calcRouteCustomParams && calcRouteCustomParams.start
			? calcRouteCustomParams.start
			: searchParameters?.formattedAddress,
		calcRouteCustomParams && calcRouteCustomParams.end
			? calcRouteCustomParams.end
			: calcRouteResult
	);

	React.useEffect(() => {
		function openCalcRouteView() {
			clearMap(geoCMSMap);
			setPage(PAGE_CALCULATE_ROUTE);
		}

		if (!fetchCalcRoute) return;

		if (skipCalcRoute) {
			setSkipCalcRoute(false);
			return;
		}

		fetchCalcRoute(TRAVEL_MODES.DRIVING)
			.then(setCalcRouteByDrivingResponse)
			.catch();
		fetchCalcRoute(TRAVEL_MODES.TRANSIT)
			.then(setCalcRouteByTransitResponse)
			.catch();
		fetchCalcRoute(TRAVEL_MODES.WALKING)
			.then(setCalcRouteByWalkingResponse)
			.catch();

		openCalcRouteView();
	}, [fetchCalcRoute, skipCalcRoute, geoCMSMap]);

	function closeCalcRouteView() {
		resetMap();
		setPage(PAGE_SERP_MAP);
		setCalcRouteResult(null);
		clearCalcRouteResponse();
		clearCalcRouteParams();
	}

	function clearCalcRouteParams() {
		if (calcRouteCustomParams) setSkipCalcRoute(true);

		setCalcRouteCustomParams(null);
	}

	function clearCalcRouteResponse() {
		setCalcRouteByDrivingResponse(null);
		setCalcRouteByTransitResponse(null);
		setCalcRouteByWalkingResponse(null);
	}

	function openStreetView() {
		setPage(PAGE_STREET_VIEW);
	}

	function handleCloseStreetView() {
		streetView.setVisible(false);
		setStreetViewResult(null);
		setPage(PAGE_SERP_MAP);
	}

	useStreetView(streetView, streetViewResult, openStreetView);

	return (
		<div
			className={styles.root}
			style={COLOR_PALETTE ? COLOR_PALETTE : null}
		>
			{isMobile &&
				(page === PAGE_LANDING ||
					page === PAGE_SERP_MAP ||
					page === PAGE_SERP_LIST ||
					page === MOBILE_INFOWINDOW_VIEW) ? (
				<Tabs
					searchFor={searchFor}
					onSearchForChange={handleSearchForChange}
				/>
			) : null}

			{page === PAGE_LANDING ? (
				<>
					{' '}
					<Splash /> {' '}
					<Intro />
				</>
			) : null}

			{page !== PAGE_CALCULATE_ROUTE &&
				page !== PAGE_STREET_VIEW &&
				page !== MOBILE_INFOWINDOW_VIEW ? (
				<SearchBarWrapper
					ref={searchBarRef}
					page={page}
					onPageChange={handlePageChange}
					onSearchSubmit={handleSearchSubmit}
					searchFor={searchFor}
					dataSrc={dataSrc}
					onSearchForChange={handleSearchForChange}
					rankedSearch={rankedSearch}
					isMunicipality={isMunicipality(searchParameters?.place)}
					onToggleFilters={onToggleFilters}
					onGeoLocationEnable={handleGeoLocationEnable}
					autocompleteResults={autocompleteResults}
					setAutocompleteResult={setAutocompleteResult}
					inputValue={mainInputValue}
					setInputValue={setMainInputValue}
					openInfoWindow={openInfoWindow}
					searchParameters={searchParameters}
					showFiltersButton={showFiltersButton}
					setRankedSorting={setRankedSorting}
				/>
			) : null}

			{page !== PAGE_STREET_VIEW && page !== MOBILE_INFOWINDOW_VIEW ? (
				<SideBar
					ref={sideBarRef}
					page={page}
					searchLabel={dataSrc.label}
					results={sortedResults}
					onResultClick={openInfoWindow}
					onCalcRouteClose={closeCalcRouteView}
					calcRouteResult={calcRouteResult}
					calcRouteResponse={[
						calcRouteByDrivingResponse,
						calcRouteByTransitgResponse,
						calcRouteByWalkingResponse
					]}
					onCalcRouteParamsChange={handleCalcRouteParamsChange}
					calcRouteCustomParams={calcRouteCustomParams}
					focusedResult={focusedResult}
					searchParameters={searchParameters}
					onResultBarScroll={panMapToResult}
				/>
			) : null}

			<Map
				page={page}
				searchParameters={searchParameters}
				map={googleMap}
				onMapLoaded={setGoogleMap}
				results={sortedResults}
				focusedResult={focusedResult}
				onToggleInfoWindow={handleToggleInfoWindow}
				onCalcRouteClick={handleCalcRouteClick}
				onStreetViewClick={handleStreetViewClick}
				onDetailClick={handleDetailClick}
				autocompleteResult={autocompleteResult}
			/>

			<Filters
				show={showFilters}
				searchFor={searchFor}
				filters={filters}
				onFiltersChange={handleFiltersChange}
				onToggleFilters={onToggleFilters}
			/>

			<ResultList
				show={page === PAGE_SERP_LIST}
				searchLabel={dataSrc.label}
				results={sortedResults}
				onCalcRouteClick={handleCalcRouteClick}
				onStreetViewClick={handleStreetViewClick}
				onDetailClick={handleDetailClick}
				rankedSorting={rankedSorting}
				setRankedSorting={setRankedSorting}
				rankedSearch={rankedSearch}
				focusedResult={focusedResult}
			/>

			{page === PAGE_STREET_VIEW ? (
				<StreetViewController
					result={streetViewResult}
					onCloseStreetView={handleCloseStreetView}
				/>
			) : null}

			{page === MOBILE_INFOWINDOW_VIEW ? (
				<InfoWindow
					result={focusedResult}
					onClose={handleToggleInfoWindow}
					onCalcRouteClick={handleCalcRouteClick}
					onStreetViewClick={handleStreetViewClick}
					onDetailClick={handleDetailClick}
				/>
			) : null}

			{detailResult ? (
				<Detail result={detailResult} onClose={handleCloseDetail} />
			) : null}

			{loading ? <Loading /> : null}
		</div>
	);
}

function Splash() {
	return <div className={styles.splash}></div>;
}

function Intro() {
	return (
		<div className={styles.intro}>
			<h3 className={styles.introTitle}>
				AXA è al tuo fianco, ovunque tu sia.
			</h3>
			<p className={styles.introSummary}>
				Scopri tutti i <strong> servizi AXA a tua disposizione.</strong>
			</p>
		</div>
	);
}

const SearchBarWrapper = React.forwardRef(({ page, ...rest }, ref) => {
	const [TCLog] = useTCLog();
	const [collapsed, setCollapsed] = React.useState(false);

	const isMobile = useDeviceDetection();

	const positionClassName =
		page === PAGE_LANDING
			? styles.searchBarWrapperCenter
			: page === PAGE_SERP_MAP
				? styles.searchBarWrapperTopRight
				: page === PAGE_SERP_LIST
					? styles.searchBarWrapperTopCenter
					: undefined;

	const className = clsx(
		styles.searchBarWrapper,
		positionClassName,
		SHOW_ALL ? styles.showAll : null
	);

	function onToggle() {
		setCollapsed(!collapsed);
	}

	const floatingToggleButtonIcon = collapsed ? (
		<Icon icon="icon/cerca_white.png" textOn="left" />
	) : (
		<Icon icon="icon/minimize_white.png" size="small" />
	);
	return (
		<div ref={ref} className={className}>
			<FloatingToggleButton
				show={page === PAGE_SERP_MAP && !isMobile}
				onClick={() => {
					TCLog(collapsed ? 'APRI_SEARCHBAR' : 'CHIUSURA_SEARCHBAR');
					onToggle();
				}}
				textOnShow="Nuova ricerca"
				type="collapse"
				enabled={!collapsed}
				icon={floatingToggleButtonIcon}
				right={!collapsed ? '' : '-90px'}
			/>
			<SearchBar collapsed={collapsed} page={page} {...rest} />
		</div>
	);
});

function getCookie(cname) {
	var name = cname + '=';
	var decodedCookie = decodeURIComponent(document.cookie);
	var ca = decodedCookie.split(';');
	for (var i = 0; i < ca.length; i++) {
		var c = ca[i];
		while (c.charAt(0) === ' ') {
			c = c.substring(1);
		}
		if (c.indexOf(name) === 0) {
			return c.substring(name.length, c.length);
		}
	}
	return '';
}
